import { useState, useEffect, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import discgoLogo from 'figma:asset/70d8fe074a29674a16552ebcf7b0d662532aa1b0.png';

const PLAYER_WEB_URL = import.meta.env.VITE_PLAYER_WEB_URL || 'http://localhost:5000';
const OWNER_WEB_URL = import.meta.env.VITE_OWNER_WEB_URL || 'http://localhost:5001';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

const SKILL_LEVELS = [
  { value: 'beginner',     label: 'Beginner',     sub: 'just getting into the sport' },
  { value: 'intermediate', label: 'Intermediate', sub: 'comfortable with the basics' },
  { value: 'advanced',     label: 'Advanced',     sub: 'competitive local player' },
  { value: 'pro',          label: 'Pro',          sub: 'tournament / professional level' },
];

type FlowStep = 'loading' | 'error' | 'password' | 'profile' | 'course' | 'welcome';

// ─── shared styles ────────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.65rem 1rem',
  border: '1.5px solid #d1d5db',
  borderRadius: '9999px',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  outline: 'none',
  background: 'white',
  color: '#111827',
  transition: 'border-color 0.15s',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  borderRadius: '12px',
  resize: 'vertical',
  minHeight: '90px',
  fontFamily: 'inherit',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  marginBottom: '0.3rem',
  color: '#374151',
  textTransform: 'lowercase',
};

const primaryBtn: React.CSSProperties = {
  width: '100%',
  padding: '0.7rem',
  background: '#2B8B3B',
  color: 'white',
  border: 'none',
  borderRadius: '9999px',
  fontSize: '0.95rem',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'opacity 0.15s',
  textTransform: 'lowercase',
};

const ghostBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#6b7280',
  fontSize: '0.875rem',
  cursor: 'pointer',
  padding: '0.4rem 0',
  textTransform: 'lowercase',
};

const errorBox: React.CSSProperties = {
  padding: '0.6rem 0.9rem',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '8px',
  color: '#dc2626',
  fontSize: '0.85rem',
};

// ─── Progress indicator ───────────────────────────────────────────────────────
function ProgressDots({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
        {steps.map((label, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  background: i < current ? '#2B8B3B' : i === current ? '#2B8B3B' : '#e5e7eb',
                  color: i <= current ? 'white' : '#9ca3af',
                  transition: 'all 0.3s',
                  flexShrink: 0,
                }}
              >
                {i < current ? '✓' : i + 1}
              </div>
              <span style={{
                fontSize: '0.65rem',
                color: i <= current ? '#2B8B3B' : '#9ca3af',
                fontWeight: i === current ? 700 : 400,
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                width: '40px',
                height: '2px',
                background: i < current ? '#2B8B3B' : '#e5e7eb',
                margin: '0 4px',
                marginBottom: '16px',
                transition: 'background 0.3s',
                flexShrink: 0,
              }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Photo upload field ───────────────────────────────────────────────────────
function PhotoField({
  label,
  preview,
  onChange,
  round,
}: {
  label: string;
  preview: string | null;
  onChange: (file: File, preview: string) => void;
  round?: boolean;
}) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={labelStyle}>{label}</label>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          cursor: 'pointer',
          padding: '0.75rem',
          border: '1.5px dashed #d1d5db',
          borderRadius: round ? '9999px' : '12px',
          background: '#f9fafb',
          transition: 'border-color 0.15s',
        }}
      >
        {preview ? (
          <img
            src={preview}
            alt="preview"
            style={{
              width: '48px',
              height: '48px',
              objectFit: 'cover',
              borderRadius: round ? '50%' : '8px',
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: round ? '50%' : '8px',
              background: '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
              flexShrink: 0,
            }}
          >
            📷
          </div>
        )}
        <div>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
            {preview ? 'tap to change photo' : 'tap to upload photo'}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
            optional · jpg, png, webp
          </p>
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const url = URL.createObjectURL(file);
            onChange(file, url);
          }}
        />
      </label>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function OnboardingPage() {
  const [step, setStep] = useState<FlowStep>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [userType, setUserType] = useState<'player' | 'owner'>('player');
  const [submitting, setSubmitting] = useState(false);

  // Step 1 – password
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Step 2 – profile
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [stateAbbr, setStateAbbr] = useState('');
  const [skillLevel, setSkillLevel] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [profileError, setProfileError] = useState('');

  // Step 3 – course (owner only)
  const [courseName, setCourseName] = useState('');
  const [courseAddress, setCourseAddress] = useState('');
  const [coursePhone, setCoursePhone] = useState('');
  const [courseWebsite, setCourseWebsite] = useState('');
  const [courseHoles, setCourseHoles] = useState('18');
  const [courseDescription, setCourseDescription] = useState('');
  const [coursePhotoFile, setCoursePhotoFile] = useState<File | null>(null);
  const [coursePhotoPreview, setCoursePhotoPreview] = useState<string | null>(null);
  const [courseError, setCourseError] = useState('');

  const settledRef = useRef(false);

  const initUser = (u: User) => {
    if (settledRef.current) return;
    settledRef.current = true;
    setUser(u);
    const t = u.user_metadata?.user_type as string;
    setUserType(t === 'owner' ? 'owner' : 'player');
    setStep('password');
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const tryInit = async () => {
      // 1. Already have a session (e.g. detectSessionInUrl already ran)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) { initUser(session.user); return; }

      // 2. PKCE code in URL — exchange manually as a fallback
      const code = new URLSearchParams(window.location.search).get('code');
      if (code) {
        try {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (!error && data?.session?.user) { initUser(data.session.user); return; }
        } catch { /* ignore */ }
      }

      // 3. Give onAuthStateChange time to fire (detectSessionInUrl fires async)
      timeoutId = setTimeout(() => {
        if (!settledRef.current) setStep('error');
      }, 8000);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) initUser(session.user);
    });

    tryInit();
    return () => { subscription.unsubscribe(); clearTimeout(timeoutId); };
  }, []);

  // Steps depend on user type
  const stepLabels = userType === 'owner'
    ? ['password', 'profile', 'course']
    : ['password', 'profile'];

  const currentIndex = { password: 0, profile: 1, course: 2 }[step as 'password' | 'profile' | 'course'] ?? 0;

  // ── handlers ────────────────────────────────────────────────────────────────

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    if (password.length < 8) { setPasswordError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setPasswordError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setStep('profile');
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to set password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const uploadPhoto = async (file: File, bucket: string, path: string): Promise<string | null> => {
    try {
      const { error } = await supabase.storage.from(bucket).upload(path, file, { upsert: true });
      if (error) return null;
      const { data } = supabase.storage.from(bucket).getPublicUrl(path);
      return data.publicUrl;
    } catch {
      return null;
    }
  };

  const handleProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    if (!phone.trim()) { setProfileError('Phone number is required.'); return; }
    if (!city.trim()) { setProfileError('City is required.'); return; }
    if (!stateAbbr) { setProfileError('State is required.'); return; }
    if (!skillLevel) { setProfileError('Skill level is required.'); return; }

    setSubmitting(true);
    try {
      let avatarUrl: string | null = null;
      if (avatarFile && user) {
        const ext = avatarFile.name.split('.').pop() || 'jpg';
        avatarUrl = await uploadPhoto(avatarFile, 'avatars', `${user.id}/avatar.${ext}`);
      }

      const updates: Record<string, unknown> = {
        phone: phone.trim(),
        city: city.trim(),
        state: stateAbbr,
        skill_level: skillLevel,
        active_role: userType === 'owner' ? 'owner' : 'player',
      };
      if (avatarUrl) updates.avatar_url = avatarUrl;

      const { error } = await supabase.from('profiles').update(updates).eq('id', user!.id);
      if (error) throw error;

      if (userType === 'owner') {
        setStep('course');
      } else {
        setStep('welcome');
        setTimeout(() => { window.location.href = `${PLAYER_WEB_URL}`; }, 2500);
      }
    } catch (err: any) {
      setProfileError(err.message || 'Failed to save profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    setCourseError('');
    if (!courseName.trim()) { setCourseError('Course name is required.'); return; }
    if (!courseAddress.trim()) { setCourseError('Course address is required.'); return; }
    if (!coursePhone.trim()) { setCourseError('Course phone number is required.'); return; }
    const holesNum = parseInt(courseHoles, 10);
    if (isNaN(holesNum) || holesNum < 1 || holesNum > 99) { setCourseError('Number of holes must be between 1 and 99.'); return; }
    if (!courseDescription.trim()) { setCourseError('Course description is required.'); return; }

    setSubmitting(true);
    try {
      let photoPath: string | null = null;
      if (coursePhotoFile && user) {
        const ext = coursePhotoFile.name.split('.').pop() || 'jpg';
        const uploaded = await uploadPhoto(coursePhotoFile, 'course-photos', `${user.id}/main.${ext}`);
        if (uploaded) photoPath = `${user.id}/main.${ext}`;
      }

      const courseRow: Record<string, unknown> = {
        owner_id: user!.id,
        name: courseName.trim(),
        description: courseDescription.trim(),
        address: courseAddress.trim(),
        location: `${city}, ${stateAbbr}`,
        holes: holesNum,
        status: 'active',
        phone: coursePhone.trim(),
      };
      if (courseWebsite.trim()) courseRow.website = courseWebsite.trim();
      if (photoPath) courseRow.photo_main_path = photoPath;

      const { error } = await supabase.from('courses').insert(courseRow);
      if (error) throw error;

      setStep('welcome');
      setTimeout(() => { window.location.href = `${OWNER_WEB_URL}`; }, 2500);
    } catch (err: any) {
      setCourseError(err.message || 'Failed to create course. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────

  const firstName = user?.user_metadata?.first_name || '';

  const shell = (children: React.ReactNode, wide = false) => (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #f0fdf4 0%, #F5F5DC 100%)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '2rem 1rem 3rem',
    }}>
      <div style={{
        width: '100%',
        maxWidth: wide ? '560px' : '460px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1.25rem',
      }}>
        <img src={discgoLogo} alt="Discgo" style={{ height: '52px' }} />
        {children}
      </div>
    </div>
  );

  const card = (children: React.ReactNode) => (
    <div style={{
      width: '100%',
      background: 'white',
      borderRadius: '16px',
      padding: '1.75rem',
      boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    }}>
      {children}
    </div>
  );

  // Loading
  if (step === 'loading') return shell(
    <div style={{ textAlign: 'center', color: '#6b7280', paddingTop: '1rem' }}>
      <div style={{
        width: '40px', height: '40px',
        border: '3px solid #d1fae5',
        borderTopColor: '#2B8B3B',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 1rem',
      }} />
      <p style={{ margin: 0, fontSize: '0.9rem' }}>setting up your account…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // Error
  if (step === 'error') return shell(
    card(
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔗</div>
        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>
          invalid or expired link
        </h2>
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.5 }}>
          this invite link has already been used, expired, or is invalid.
          please contact your administrator for a new invitation.
        </p>
        <a
          href="mailto:support@discgo.app"
          style={{ color: '#2B8B3B', fontSize: '0.875rem', fontWeight: 600 }}
        >
          contact support
        </a>
      </div>
    )
  );

  // Welcome
  if (step === 'welcome') return shell(
    <div style={{ textAlign: 'center', paddingTop: '1rem' }}>
      <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🥏</div>
      <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.6rem', fontWeight: 800, color: '#111827', textTransform: 'lowercase' }}>
        welcome{firstName ? `, ${firstName.toLowerCase()}` : ''}!
      </h1>
      <p style={{ margin: '0 0 1.5rem', fontSize: '1rem', color: '#4b5563', lineHeight: 1.6, maxWidth: '320px' }}>
        {userType === 'owner'
          ? "your course is live on discgo. time to tee it up."
          : "you're all set. time to find your next round."}
      </p>
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        color: '#2B8B3B',
        fontSize: '0.875rem',
        fontWeight: 500,
      }}>
        <div style={{
          width: '14px', height: '14px',
          border: '2px solid #d1fae5',
          borderTopColor: '#2B8B3B',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        taking you to your dashboard…
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // Step 1 – Password
  if (step === 'password') return shell(
    card(
      <>
        <ProgressDots steps={stepLabels} current={currentIndex} />
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem', fontWeight: 700, color: '#111827', textTransform: 'lowercase' }}>
          {firstName ? `hey ${firstName.toLowerCase()}, ` : ''}set your password
        </h2>
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
          choose a secure password for your discgo account.
        </p>

        <form onSubmit={handlePassword} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <label htmlFor="ob-pw" style={labelStyle}>password</label>
            <input
              id="ob-pw"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="at least 8 characters"
              autoComplete="new-password"
              style={inputStyle}
            />
          </div>
          <div>
            <label htmlFor="ob-cpw" style={labelStyle}>confirm password</label>
            <input
              id="ob-cpw"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="repeat your password"
              autoComplete="new-password"
              style={inputStyle}
            />
          </div>

          {passwordError && <div style={errorBox}>{passwordError}</div>}

          <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.65 : 1, marginTop: '0.25rem' }}>
            {submitting ? 'setting password…' : 'continue →'}
          </button>
        </form>
      </>
    )
  );

  // Step 2 – Profile
  if (step === 'profile') return shell(
    card(
      <>
        <ProgressDots steps={stepLabels} current={currentIndex} />
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem', fontWeight: 700, color: '#111827', textTransform: 'lowercase' }}>
          your profile
        </h2>
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
          {userType === 'owner'
            ? 'tell us about yourself. your course comes next.'
            : 'a few quick details so we can find you the best rounds.'}
        </p>

        <form onSubmit={handleProfile} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <PhotoField
            label="profile photo"
            preview={avatarPreview}
            onChange={(file, preview) => { setAvatarFile(file); setAvatarPreview(preview); }}
            round
          />

          <div>
            <label htmlFor="ob-phone" style={labelStyle}>phone number</label>
            <input
              id="ob-phone"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem' }}>
            <div>
              <label htmlFor="ob-city" style={labelStyle}>city</label>
              <input
                id="ob-city"
                type="text"
                value={city}
                onChange={e => setCity(e.target.value)}
                placeholder="Portland"
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="ob-state" style={labelStyle}>state</label>
              <select
                id="ob-state"
                value={stateAbbr}
                onChange={e => setStateAbbr(e.target.value)}
                style={{ ...inputStyle, borderRadius: '9999px', appearance: 'auto', paddingRight: '0.5rem', width: '80px' }}
              >
                <option value="">—</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>skill level</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {SKILL_LEVELS.map(sl => (
                <label
                  key={sl.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.6rem 1rem',
                    border: `1.5px solid ${skillLevel === sl.value ? '#2B8B3B' : '#e5e7eb'}`,
                    borderRadius: '9999px',
                    cursor: 'pointer',
                    background: skillLevel === sl.value ? '#f0fdf4' : 'white',
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="radio"
                    name="skill"
                    value={sl.value}
                    checked={skillLevel === sl.value}
                    onChange={() => setSkillLevel(sl.value)}
                    style={{ accentColor: '#2B8B3B' }}
                  />
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827', textTransform: 'lowercase' }}>
                      {sl.label}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#6b7280', marginLeft: '0.4rem' }}>
                      — {sl.sub}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {profileError && <div style={errorBox}>{profileError}</div>}

          <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.65 : 1, marginTop: '0.25rem' }}>
            {submitting
              ? 'saving…'
              : userType === 'owner' ? 'continue to course setup →' : 'finish setup →'}
          </button>
          <button type="button" onClick={() => setStep('password')} style={ghostBtn}>
            ← back
          </button>
        </form>
      </>
    ),
    true
  );

  // Step 3 – Course (owner only)
  if (step === 'course') return shell(
    card(
      <>
        <ProgressDots steps={stepLabels} current={currentIndex} />
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem', fontWeight: 700, color: '#111827', textTransform: 'lowercase' }}>
          your course
        </h2>
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
          add your course details. players will see this on discgo.
        </p>

        <form onSubmit={handleCourse} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <PhotoField
            label="course photo"
            preview={coursePhotoPreview}
            onChange={(file, preview) => { setCoursePhotoFile(file); setCoursePhotoPreview(preview); }}
          />

          <div>
            <label htmlFor="ob-cname" style={labelStyle}>course name</label>
            <input
              id="ob-cname"
              type="text"
              value={courseName}
              onChange={e => setCourseName(e.target.value)}
              placeholder="Pine Ridge Disc Golf"
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="ob-caddr" style={labelStyle}>course address</label>
            <input
              id="ob-caddr"
              type="text"
              value={courseAddress}
              onChange={e => setCourseAddress(e.target.value)}
              placeholder="123 Main St, Portland, OR 97201"
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label htmlFor="ob-cphone" style={labelStyle}>course phone</label>
              <input
                id="ob-cphone"
                type="tel"
                value={coursePhone}
                onChange={e => setCoursePhone(e.target.value)}
                placeholder="(555) 123-4567"
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="ob-holes" style={labelStyle}>number of holes</label>
              <input
                id="ob-holes"
                type="number"
                min="1"
                max="99"
                value={courseHoles}
                onChange={e => setCourseHoles(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label htmlFor="ob-cweb" style={labelStyle}>website <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
            <input
              id="ob-cweb"
              type="url"
              value={courseWebsite}
              onChange={e => setCourseWebsite(e.target.value)}
              placeholder="https://pineridgediscgolf.com"
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="ob-cdesc" style={labelStyle}>course description</label>
            <textarea
              id="ob-cdesc"
              value={courseDescription}
              onChange={e => setCourseDescription(e.target.value)}
              placeholder="Tell players what makes your course special — terrain, vibe, amenities, etc."
              style={textareaStyle}
            />
          </div>

          {courseError && <div style={errorBox}>{courseError}</div>}

          <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.65 : 1, marginTop: '0.25rem' }}>
            {submitting ? 'finishing setup…' : 'finish setup →'}
          </button>
          <button type="button" onClick={() => setStep('profile')} style={ghostBtn}>
            ← back
          </button>
        </form>
      </>
    ),
    true
  );

  return null;
}
